import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Boxes, AlertTriangle, Printer, ArrowLeft, CircleDollarSign, PlayCircle,
  Package, Truck, CheckCircle2, Wrench, FlaskConical, Save, Plus, Clock, Bot, User, Cpu, ShieldAlert,
} from 'lucide-react';
import {
  hardwareApi, money, shortDate, UNIT_STATUS_CHIP, UNIT_STATUS_LABEL,
  type AgreementAuditRow, type DeviceModelRow, type DeviceUnitRow, type UnitEvent,
} from '../../services/hardwareApi';
import { inventoryApi, type DamageRow, type VendorRow } from '../../services/inventoryApi';
import ShimmerImage from '../../components/ShimmerImage';
import { toastService } from '../../services/toastService';
import JourneyTimeline from './JourneyTimeline';
import { InventoryPage } from './InventoryChrome';
import DamageResolveActions, { type DamageResolveResult } from './DamageResolveActions';
import { printUnitLabel } from '../../utils/unitLabelPrint';
import UnitNotesEditor, { deltaToPlainText, type UnitNotesValue } from './UnitNotesEditor';
import { unitBadgeLabel } from './unitBadgeSafe';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * ONE UNIT — mockup screen B. Tracking id, serial, model, the label as the
 * Rollo prints it, financing, the LIFECYCLE RAIL, the provisioning
 * checklist (per-model template, snapshotted onto this unit), editable
 * identity fields, and the full timeline (B2's `/lifecycle/units/{id}/timeline`).
 */

/**
 * Ids here (`unit_<uuid>`, `ord_…`, `grn_…`, `ven_…`, a Cognito sub) are machine
 * keys, not something an operator should have to read off the screen. Show a
 * short scannable tail — `UNIT-A9BFD942` — and keep the full value on hover so
 * it can still be copied when someone genuinely needs it.
 */
const shortRef = (id?: string): string => {
  if (!id) return '—';
  const [prefix, ...rest] = id.split('_');
  const body = (rest.join('_') || prefix).replace(/-/g, '');
  const tail = body.slice(0, 8).toUpperCase();
  return rest.length ? `${prefix.toUpperCase()}-${tail}` : tail;
};

/** True for a bare uuid / Cognito sub — worth hiding rather than shortening. */
const isOpaqueId = (v?: string) => !!v && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v);

/** Prefer a human name; an opaque sub reads as nothing useful, so drop it. */
const personLabel = (v?: string): string | null => (!v || isOpaqueId(v) ? null : v);

const barcodeBars = (seed: string) => {
  const widths: number[] = [];
  for (let i = 0; i < 28; i++) {
    const c = seed.charCodeAt(i % seed.length) || 1;
    widths.push((c % 3) + 1);
  }
  return widths;
};

// ---------------------------------------------------------------------------
// Lifecycle rail
// ---------------------------------------------------------------------------

const RAIL_STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'in_stock', label: 'In stock' },
  { key: 'allocated', label: 'Allocated' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'configured', label: 'Configured' },
  { key: 'packing', label: 'Packed (box)' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
] as const;

/** Maps a unit's fulfilment state onto the rail. `null` for exception states — those get an evidence-based estimate instead of a false "now". */
const STATE_TO_RAIL: Record<string, string | null> = {
  in_stock: 'in_stock',
  allocated: 'allocated',
  awaiting: 'allocated',
  processing: 'allocated',
  provisioning: 'provisioning',
  configured: 'configured',
  packing: 'packing',
  shipped: 'shipped',
  delivered: 'delivered',
  deployed: 'delivered',
  on_hold: null,
  backordered: null,
  returned: null,
  rma: null,
  retired: null,
};

function railProgress(unit: DeviceUnitRow): { doneThrough: number; nowIndex: number | null } {
  const railKey = STATE_TO_RAIL[unit.state];
  if (railKey) {
    const idx = RAIL_STEPS.findIndex((s) => s.key === railKey);
    // 'deployed' reads as fully complete — nothing left to highlight as "now".
    if (unit.state === 'deployed') return { doneThrough: RAIL_STEPS.length - 1, nowIndex: null };
    return { doneThrough: idx - 1, nowIndex: idx };
  }
  // Exception state — best-effort progress from what is actually stored, never a guess dressed up as fact.
  let idx = 1; // 'received' + 'in_stock' at minimum — the unit exists
  if (unit.shipment?.shippedAt) idx = RAIL_STEPS.findIndex((s) => s.key === 'shipped');
  else if (unit.boxNo) idx = RAIL_STEPS.findIndex((s) => s.key === 'packing');
  else if (unit.provisioning) {
    const required = unit.provisioning.steps.filter((s) => s.required);
    const allDone = required.length === 0 || required.every((s) => s.done);
    idx = RAIL_STEPS.findIndex((s) => s.key === (allDone ? 'configured' : 'provisioning'));
  } else if (unit.orderId) idx = RAIL_STEPS.findIndex((s) => s.key === 'allocated');
  return { doneThrough: idx, nowIndex: null };
}

const LifecycleRail: React.FC<{ unit: DeviceUnitRow }> = ({ unit }) => {
  const { doneThrough, nowIndex } = useMemo(() => railProgress(unit), [unit]);
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4">
      <div className="flex items-center flex-wrap">
        {RAIL_STEPS.map((step, i) => {
          const done = i <= doneThrough;
          const now = i === nowIndex;
          let extra = '';
          if (step.key === 'provisioning' && now && unit.provisioning) {
            const required = unit.provisioning.steps.filter((s) => s.required);
            const doneCount = required.filter((s) => s.done).length;
            extra = ` ${doneCount}/${required.length}`;
          }
          return (
            <React.Fragment key={step.key}>
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                  now ? 'text-white' : done ? 'bg-emerald-50 border-2 border-emerald-400 text-emerald-700' : 'bg-white border-2 border-stone-200 text-stone-400'
                }`} style={now ? { background: 'linear-gradient(135deg,#DC143C,#9F1239)' } : undefined}>
                  {done ? '✓' : i + 1}
                </div>
                <span className={`text-[10.5px] font-semibold whitespace-nowrap ${now ? 'text-stone-900' : 'text-stone-500'}`}>
                  {step.label}{extra}
                </span>
              </div>
              {i < RAIL_STEPS.length - 1 && <div className="w-6 h-0.5 bg-stone-200 mx-1.5 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>
      {nowIndex === null && (
        <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Currently <strong>{UNIT_STATUS_LABEL[unit.state]}</strong>{unit.holdReason ? ` — ${unit.holdReason}` : ''}. Progress above is a
          best-effort estimate from what is on record, not a claim about where in the forward pipeline this device is.
        </p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Timeline
// ---------------------------------------------------------------------------

const SOURCE_CHIP: Record<string, string> = {
  system: 'bg-stone-100 text-stone-500 border-stone-200',
  admin: 'bg-blue-100 text-blue-700 border-blue-200',
  device: 'bg-rose-100 text-rose-700 border-rose-200',
};
const SOURCE_ICON: Record<string, React.FC<any>> = { system: Cpu, admin: User, device: Bot };

const KIND_ICON = (kind: string): React.FC<any> => {
  if (kind.startsWith('provisioning')) return Wrench;
  if (kind.startsWith('box') || kind === 'received') return Package;
  if (kind.startsWith('os_') || kind.startsWith('build_')) return FlaskConical;
  if (kind === 'agreement_action') return CircleDollarSign;
  return Clock;
};

const TimelinePanel: React.FC<{ unitId: string; refreshKey?: number }> = ({ unitId, refreshKey }) => {
  const [items, setItems] = useState<UnitEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await hardwareApi.getUnitTimeline(unitId, { limit: 20 });
      setItems(res.items || []);
      setNextToken(res.nextToken);
    } catch (e: any) {
      setError(e?.message || 'Failed to load the timeline');
    } finally {
      setLoading(false);
    }
  }, [unitId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const loadOlder = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await hardwareApi.getUnitTimeline(unitId, { limit: 20, nextToken });
      setItems((prev) => [...prev, ...(res.items || [])]);
      setNextToken(res.nextToken);
    } catch (e: any) {
      setError(e?.message || 'Failed to load older entries');
    } finally {
      setLoadingMore(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await hardwareApi.addUnitEvent(unitId, { kind: 'note', label: note.trim(), source: 'admin' });
      setNote('');
      setNoteOpen(false);
      await load();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to add note');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-stone-800">Timeline</h3>
        <button
          onClick={() => setNoteOpen((o) => !o)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-stone-300 text-stone-700 text-[11px] font-semibold"
        >
          <Plus size={12} /> Add note
        </button>
      </div>

      {noteOpen && (
        <div className="mb-3 flex gap-2">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What's worth noting on this unit?"
            className="flex-1 px-3 py-2 border border-stone-200 rounded-lg text-xs min-h-[36px]"
          />
          <button
            onClick={addNote}
            disabled={savingNote || !note.trim()}
            className="px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50 min-h-[36px]"
            style={{ background: '#DC143C' }}
          >
            {savingNote ? <RubyGemMark size={13} drawing color="currentColor" /> : 'Save'}
          </button>
        </div>
      )}

      {error && <p className="text-xs text-red-700 mb-2">{error}</p>}

      {loading ? (
        <RubyLoader variant="section" />
      ) : items.length === 0 ? (
        <p className="text-xs text-stone-400 py-4">No timeline entries yet.</p>
      ) : (
        <div className="space-y-0">
          {items.map((ev, i) => {
            const Icon = KIND_ICON(ev.kind);
            const SourceIcon = SOURCE_ICON[ev.source] || Clock;
            return (
              <div key={`${ev.kind}-${ev.at}-${i}`} className="flex gap-3 py-2.5 border-b border-stone-100 last:border-b-0">
                <div className="w-7 h-7 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center shrink-0">
                  <Icon size={13} className="text-stone-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-stone-800">{ev.label}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[9.5px] font-semibold rounded-full px-1.5 py-0.5 border ${SOURCE_CHIP[ev.source] || 'bg-stone-100 text-stone-500 border-stone-200'}`}>
                      <SourceIcon size={9} /> {ev.source}
                    </span>
                    {personLabel(ev.actor) && <span>· {personLabel(ev.actor)}</span>}
                    <span>· {shortDate(ev.at)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nextToken && (
        <div className="pt-3 text-center">
          <button
            onClick={loadOlder}
            disabled={loadingMore}
            className="px-4 py-2 rounded-lg border border-stone-300 bg-white text-stone-700 text-xs font-semibold disabled:opacity-50"
          >
            {loadingMore ? <span className="inline-flex"><RubyGemMark size={13} drawing color="currentColor" /></span> : 'Load older'}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Active damage — damage lifecycle wave. Only renders when the unit has at
// least one OPEN damage report; the same inline resolve/cancel actions the
// Damage tab uses live right here, next to the device they act on.
// ---------------------------------------------------------------------------

const ActiveDamageCard: React.FC<{
  unitId: string;
  onUnitChanged: (u: DeviceUnitRow) => void;
  onResolved: () => void;
}> = ({ unitId, onUnitChanged, onResolved }) => {
  const [rows, setRows] = useState<DamageRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryApi.listDamage({ unitId, status: 'open' });
      setRows(res.items || []);
    } catch {
      // A failed fetch here should never block the rest of the unit page —
      // the Damage tab remains the fallback place to see this.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [unitId]);
  useEffect(() => { load(); }, [load]);

  const handleResolved = (result: DamageResolveResult) => {
    setRows((prev) => prev.filter((d) => d.createdAt !== result.damage.createdAt));
    if (result.unit) onUnitChanged(result.unit as DeviceUnitRow);
    onResolved();
  };

  if (loading || rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-5">
      <h3 className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-3">
        <ShieldAlert size={16} /> Active damage — {rows.length} open report{rows.length === 1 ? '' : 's'}
      </h3>
      <div className="space-y-3">
        {rows.map((d) => (
          <div key={d.createdAt} className="rounded-lg border border-amber-200 bg-white p-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="text-xs text-stone-700 min-w-0">
                <div className="font-semibold">{d.reason}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  {d.stage} · charged to {d.chargedTo}
                  {typeof d.amountCents === 'number' ? ` · $${(d.amountCents / 100).toFixed(2)}` : ''}
                  {' · '}{shortDate(d.createdAt)}
                </div>
              </div>
              <DamageResolveActions damage={d} onResolved={handleResolved} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Provisioning checklist
// ---------------------------------------------------------------------------

const ProvisioningPanel: React.FC<{ unit: DeviceUnitRow; onChanged: (u: DeviceUnitRow) => void }> = ({ unit, onChanged }) => {
  const [starting, setStarting] = useState(false);
  const [ticking, setTicking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canStart = !unit.provisioning && (unit.state === 'in_stock' || unit.state === 'allocated');

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await hardwareApi.startProvisioning(unit.unitId);
      onChanged(res.unit);
      toastService.success('Provisioning started.');
    } catch (e: any) {
      setError(e?.message || 'Could not start provisioning');
    } finally {
      setStarting(false);
    }
  };

  const tick = async (stepId: string, done: boolean) => {
    setTicking(stepId);
    setError(null);
    try {
      const res = await hardwareApi.tickProvisioningStep(unit.unitId, stepId, done);
      onChanged(res.unit);
    } catch (e: any) {
      setError(e?.message || 'Could not update this step');
    } finally {
      setTicking(null);
    }
  };

  const grouped = useMemo(() => {
    if (!unit.provisioning) return [];
    const order: string[] = [];
    const map = new Map<string, typeof unit.provisioning.steps>();
    for (const s of unit.provisioning.steps) {
      if (!map.has(s.category)) { map.set(s.category, []); order.push(s.category); }
      map.get(s.category)!.push(s);
    }
    return order.map((cat) => ({ category: cat, steps: map.get(cat)! }));
  }, [unit.provisioning]);

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-bold text-stone-800">Provisioning checklist</h3>
        {canStart && (
          <button
            onClick={start}
            disabled={starting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
            style={{ background: '#DC143C' }}
          >
            {starting ? <RubyGemMark size={13} drawing color="currentColor" /> : <PlayCircle size={13} />} Start provisioning
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-700 mb-2">{error}</p>}

      {!unit.provisioning ? (
        <p className="text-xs text-stone-500 py-3">
          {canStart
            ? "Not started yet — snapshots this model's provisioning template the moment it begins."
            : `Provisioning is only available from in-stock or allocated (this unit is ${UNIT_STATUS_LABEL[unit.state].toLowerCase()}).`}
        </p>
      ) : grouped.length === 0 ? (
        <p className="text-xs text-stone-500 py-3">This model's template has no steps — provisioning auto-completed.</p>
      ) : (
        <div className="space-y-3">
          {grouped.map((g) => (
            <div key={g.category}>
              <div className="text-[9px] uppercase tracking-wider text-stone-400 mb-1.5">{g.category}</div>
              <div className="space-y-1.5">
                {g.steps.map((s) => (
                  <div
                    key={s.stepId}
                    className={`rounded-lg border text-xs ${s.done ? 'border-emerald-200 bg-emerald-50' : 'border-stone-200 bg-white'}`}
                  >
                    <div className="flex items-center gap-2.5 px-3 py-2">
                      <button
                        onClick={() => tick(s.stepId, !s.done)}
                        disabled={ticking === s.stepId}
                        className={`w-[18px] h-[18px] rounded flex items-center justify-center text-[10px] shrink-0 ${
                          s.done ? 'bg-emerald-500 text-white' : 'border-2 border-stone-300'
                        }`}
                      >
                        {ticking === s.stepId ? <RubyGemMark size={10} drawing color="currentColor" /> : (s.done ? '✓' : '')}
                      </button>
                      <span className="flex-1 text-stone-700">{s.label}{!s.required && <span className="text-stone-400"> (optional)</span>}</span>
                      {s.done && (s.at || personLabel(s.by)) && (
                        <span className="text-[9.5px] text-stone-400" title={s.by || undefined}>
                          {[personLabel(s.by), s.at ? shortDate(s.at) : null].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    {/* THE HOW-TO, WHERE THE WORK HAPPENS.
                        Shown expanded rather than behind a click: this exists so
                        someone doing the step for the first time can follow it,
                        and an instruction they have to go looking for is one they
                        will skip. Only steps that HAVE instructions render this,
                        so a familiar checklist stays compact.
                        `whitespace-pre-line` so line breaks the author typed
                        survive — a numbered procedure is the common case. */}
                    {s.instructions && (
                      <div className="border-t border-stone-100 px-3 py-2 pl-[42px]">
                        <p className="whitespace-pre-line text-[11px] leading-relaxed text-stone-600">
                          {s.instructions}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <p className="text-[10.5px] text-stone-500 pt-1">
            All required steps ticked ⇒ auto-advances to <strong>configured</strong>. Editing the model's template later never
            rewrites this unit — it already snapshotted its own copy.
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Identity & provenance (editable)
// ---------------------------------------------------------------------------

const IdentityPanel: React.FC<{ unit: DeviceUnitRow; onSaved: (u: DeviceUnitRow) => void }> = ({ unit, onSaved }) => {
  const [serial, setSerial] = useState(unit.serialNumber || '');
  const [mac, setMac] = useState(unit.mac || '');
  const [osVersion, setOsVersion] = useState(unit.osVersion || '');
  const [appBuild, setAppBuild] = useState(unit.appBuild || '');
  const [firmwareNotes, setFirmwareNotes] = useState(unit.firmwareNotes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSerial(unit.serialNumber || ''); setMac(unit.mac || '');
    setOsVersion(unit.osVersion || ''); setAppBuild(unit.appBuild || '');
    setFirmwareNotes(unit.firmwareNotes || '');
  }, [unit.unitId, unit.serialNumber, unit.mac, unit.osVersion, unit.appBuild, unit.firmwareNotes]);

  const dirty = serial !== (unit.serialNumber || '') || mac !== (unit.mac || '')
    || osVersion !== (unit.osVersion || '') || appBuild !== (unit.appBuild || '')
    || firmwareNotes !== (unit.firmwareNotes || '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await hardwareApi.upsertUnit({
        unitId: unit.unitId,
        serialNumber: serial.trim() || undefined,
        mac: mac.trim() || undefined,
        osVersion: osVersion.trim() || undefined,
        appBuild: appBuild.trim() || undefined,
        firmwareNotes: firmwareNotes.trim() || undefined,
      });
      onSaved(res.unit);
      toastService.success('Identity saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <h3 className="text-sm font-bold text-stone-800 mb-3">Identity & provenance</h3>
      <div className="space-y-2.5">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Serial</label>
          <input value={serial} onChange={(e) => setSerial(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs font-mono min-h-[36px]" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">MAC address</label>
          <input value={mac} onChange={(e) => setMac(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs font-mono min-h-[36px]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">OS version</label>
            <input value={osVersion} onChange={(e) => setOsVersion(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs font-mono min-h-[36px]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">App build</label>
            <input value={appBuild} onChange={(e) => setAppBuild(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs font-mono min-h-[36px]" />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-stone-500 mb-1 font-mono">Firmware / notes</label>
          <textarea value={firmwareNotes} onChange={(e) => setFirmwareNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs resize-none" />
        </div>
        {unit.lastSeenAt && <p className="text-[10.5px] text-stone-400">Last seen {shortDate(unit.lastSeenAt)}</p>}
        {error && <p className="text-xs text-red-700">{error}</p>}
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-40 min-h-[36px]"
          style={{ background: '#DC143C' }}
        >
          {saving ? <RubyGemMark size={13} drawing color="currentColor" /> : <Save size={13} />} Save identity
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

/**
 * The receiving card's editor, on the device's own page.
 *
 * Deliberately the same component: a note written at receipt and one written
 * six months later are the same kind of statement about the same device, and
 * two editors would drift in what they allow and how they store it.
 */
const UnitConditionPanel: React.FC<{
  unit: DeviceUnitRow;
  onSaved: (u: DeviceUnitRow) => void;
}> = ({ unit, onSaved }) => {
  const [value, setValue] = useState<UnitNotesValue>(() => ({
    notes: unit.notes || '',
    notesRich: (unit.notesRich as any) || { ops: [{ insert: `${unit.notes || ''}\n` }] },
    badges: unit.badges || [],
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const badgesChanged = JSON.stringify([...value.badges].sort())
    !== JSON.stringify([...(unit.badges || [])].sort());
  const dirty = value.notes.trim() !== (unit.notes || '').trim() || badgesChanged;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const res = await hardwareApi.upsertUnit({
        unitId: unit.unitId,
        modelId: unit.modelId,
        notes: deltaToPlainText(value.notesRich),
        notesRich: value.notesRich,
        badges: value.badges,
      });
      onSaved(res.unit);
      toastService.success('Condition saved.');
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <h3 className="mb-3 text-sm font-bold text-stone-800">Condition &amp; notes</h3>
      <UnitNotesEditor value={value} onChange={setValue} compact />
      {error && <p className="mt-2 text-[12px] font-medium text-[#DC143C]">{error}</p>}
      <div className="mt-3 flex justify-end border-t border-stone-200 pt-3">
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="min-h-[40px] rounded-lg px-4 text-[12.5px] font-semibold text-white disabled:opacity-40"
          style={{ background: '#DC143C' }}
        >{saving ? 'Saving…' : dirty ? 'Save condition' : 'Saved'}</button>
      </div>
    </div>
  );
};

/**
 * ONE IMPLEMENTATION, SHOWN IN TWO PLACES.
 *
 * `/inventory/units/:unitId` is the standalone route and is unchanged: with no
 * props this component behaves exactly as it always has, reading the id off the
 * route and rendering its own `InventoryPage` chrome.
 *
 * The device page's Unit tab renders this SAME component with `unitId` passed
 * and `embedded` set, which drops only the chrome that would double up inside
 * that page — the black header, the breadcrumb, the back link, the outer
 * padding. Every panel below is identical. A second implementation of these
 * panels would drift, and the two screens would start disagreeing about one
 * device.
 */
export interface UnitPageProps {
  /** The unit to show. Falls back to the route param when not given. */
  unitId?: string;
  /** Render the panels only, with no page-level chrome around them. */
  embedded?: boolean;
}

const UnitPage: React.FC<UnitPageProps> = ({ unitId: unitIdProp, embedded }) => {
  const { unitId: unitIdFromRoute } = useParams<{ unitId: string }>();
  const unitId = unitIdProp ?? unitIdFromRoute;
  const navigate = useNavigate();
  const [unit, setUnit] = useState<DeviceUnitRow | null>(null);
  const [audit, setAudit] = useState<AgreementAuditRow[]>([]);
  const [models, setModels] = useState<DeviceModelRow[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!unitId) return;
    setLoading(true); setError(null);
    try {
      const [u, m, v] = await Promise.all([
        hardwareApi.getUnit(unitId),
        hardwareApi.listModels().catch(() => ({ items: [] as DeviceModelRow[] })),
        // Only so the vendor reads as a name rather than `ven_<uuid>`; a failure
        // here must never cost us the unit.
        inventoryApi.listVendors().catch(() => ({ items: [] as VendorRow[] })),
      ]);
      setUnit(u.unit);
      setAudit(u.audit || []);
      setModels(m.items || []);
      setVendors(v.items || []);
    } catch (e: any) {
      setError(e?.message || 'Could not load this unit');
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  useEffect(() => { load(); }, [load]);

  const model = useMemo(() => models.find((m) => m.modelId === unit?.modelId), [models, unit]);
  const vendorName = useMemo(
    () => vendors.find((v) => v.vendorId === unit?.vendorId)?.name || model?.vendor?.supplierName || null,
    [vendors, unit, model],
  );
  const bars = useMemo(() => barcodeBars(unitId || 'RTA'), [unitId]);

  /**
   * What the history's ids mean, for reading only — the serial is how a device
   * is spoken about, `u_…` is how it is stored. See `humanise` in the timeline:
   * the entries themselves are never rewritten.
   */
  const timelineLabels = useMemo(() => {
    const m: Record<string, string> = {};
    if (unit?.unitId && unit.serialNumber) m[unit.unitId] = `S/N ${unit.serialNumber}`;
    if (unit?.modelId && model?.modelName) m[unit.modelId] = model.modelName;
    return m;
  }, [unit?.unitId, unit?.serialNumber, unit?.modelId, model?.modelName]);

  /**
   * The page frame — real chrome on the standalone route, nothing at all when
   * embedded. Keeping this in one place is what makes "same panels, two hosts"
   * true rather than aspirational: everything below the frame is shared.
   */
  const frame = (
    opts: { title: string; subtitle?: string; actions?: React.ReactNode },
    children: React.ReactNode,
  ) => (embedded
    ? (
      <div>
        {opts.actions && <div className="mb-4 flex justify-end">{opts.actions}</div>}
        {children}
      </div>
    )
    : (
      <InventoryPage icon={Boxes} title={opts.title} subtitle={opts.subtitle} actions={opts.actions}>
        {children}
      </InventoryPage>
    ));

  if (loading) {
    return frame(
      { title: shortRef(unitId) },
      <RubyLoader variant="page" />,
    );
  }

  if (error || !unit) {
    return frame(
      { title: shortRef(unitId) },
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
        <p className="text-sm text-red-800">{error || 'Unit not found'}</p>
        {/* Embedded, this link would take someone out of the device page they
            are standing on — the standalone route is where it belongs. */}
        {!embedded && (
          <button onClick={() => navigate('/inventory/catalog')} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-red-600">
            <ArrowLeft size={14} /> Back to Inventory Hub
          </button>
        )}
      </div>,
    );
  }

  return frame(
    {
      title: model?.modelName || 'Unit',
      subtitle: `${shortRef(unit.unitId)}${unit.serialNumber ? ` · S/N ${unit.serialNumber}` : ''}`,
      actions: (
        <button
          onClick={async () => {
            setPrinting(true);
            try {
              await printUnitLabel(unit.unitId);
            } catch (e: any) {
              setError(e?.message || 'Could not prepare the label');
            } finally {
              setPrinting(false);
            }
          }}
          disabled={printing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium min-h-[44px] disabled:opacity-50"
          style={{ background: '#DC143C' }}
        >
          {printing ? <RubyGemMark size={16} drawing color="currentColor" /> : <Printer size={16} />} Print label
        </button>
      ),
    },
    <div className="space-y-5">
        <LifecycleRail unit={unit} />
        <ActiveDamageCard
          unitId={unit.unitId}
          onUnitChanged={setUnit}
          onResolved={() => setTimelineRefreshKey((k) => k + 1)}
        />

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-5 items-start">
          <div className="space-y-5">
            {/* Header card */}
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-4 flex-wrap">
                <ShimmerImage
                  src={model?.media?.heroImageUrl || model?.media?.photos?.[0]}
                  alt={model?.modelName || unit.modelId}
                  width={64}
                  height={64}
                  objectFit="contain"
                  variant="light"
                  className="shrink-0 rounded-2xl border border-stone-200 bg-stone-50"
                  fallbackBgColor="bg-stone-50"
                  fallbackIcon={<Boxes size={28} className="text-stone-300" />}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-lg font-bold text-stone-900">
                    {model?.modelName || shortRef(unit.modelId)}{' '}
                    <span className="font-mono text-sm text-emerald-600" title={unit.unitId}>{shortRef(unit.unitId)}</span>
                  </div>
                  <p className="text-[11px] text-stone-500 mt-1">
                    S/N {unit.serialNumber || '—'} · vendor {vendorName || '—'} · cost {money(unit.costCents ?? model?.landedCostCents)}
                  </p>
                </div>
                <span className={`shrink-0 text-[11px] font-semibold rounded px-2.5 py-1 ${UNIT_STATUS_CHIP[unit.state] || 'bg-gray-200 text-gray-700'}`}>
                  <span title={unit.orderId || undefined}>
                    {UNIT_STATUS_LABEL[unit.state] || unit.state}{unit.orderId ? ` → ${shortRef(unit.orderId)}` : ''}
                  </span>
                </span>
              </div>

              <div className="mt-5 flex items-center gap-4 flex-wrap">
                <div className="bg-white text-black rounded-md p-3 shadow-xl border border-stone-300" style={{ width: 288 }}>
                  <div className="flex justify-between items-baseline">
                    <span className="text-[9px] font-extrabold tracking-wide uppercase">{model?.manufacturer || 'RubyTill'} · {model?.modelName || unit.modelId}</span>
                    <span className="text-[8px] font-mono">S/N {unit.serialNumber || '—'}</span>
                  </div>
                  <div className="flex items-end gap-[1.5px] my-1" style={{ height: 26 }}>
                    {bars.map((w, i) => (
                      <div key={i} style={{ width: w, height: '100%', background: '#111' }} />
                    ))}
                  </div>
                  <div className="text-center font-mono text-[10px] font-bold tracking-[0.2em]">{unit.unitId}</div>
                </div>
                <p className="text-[11px] text-stone-500 max-w-xs">
                  Rollo 2"×0.51". Combined on the label: brand + model, serial, company tracking ID — no prices.
                  Scanning it anywhere (Stock tab's lookup box) jumps straight to this page.
                </p>
              </div>
            </div>

            <ProvisioningPanel unit={unit} onChanged={setUnit} />

            {/* Financing */}
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-stone-200 bg-stone-50 flex items-center gap-2">
                <CircleDollarSign size={15} className="text-stone-500" />
                <span className="text-xs font-bold text-stone-700 uppercase tracking-wide">Financing on this unit</span>
              </div>
              {unit.agreement ? (
                <div className="p-4 flex items-center justify-between gap-3 flex-wrap text-sm">
                  <div>
                    <div className="text-stone-800 font-medium">
                      <span title={unit.orderId || undefined}>{shortRef(unit.orderId)}</span> · {unit.agreement.saleMode} · {money(unit.agreement.monthlyAmountCents)}/mo
                      {unit.agreement.termMonths ? ` × ${unit.agreement.termMonths}` : unit.agreement.saleMode === 'rental' ? ' · open-ended' : ''}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      Status: {unit.agreement.status}
                      {(unit.agreement.depositCents || 0) > 0 && (
                        <span className="ml-2 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                          deposit {money(unit.agreement.depositCents || 0)}{typeof unit.agreement.depositRefundedCents === 'number' ? ` · ${money(unit.agreement.depositRefundedCents)} refunded` : ' held'}
                        </span>
                      )}
                    </div>
                    {unit.returnExpectedAt && unit.state !== 'returned' && unit.state !== 'in_stock' && (
                      <span className="mt-1 inline-block rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                        ⏳ Return expected since {new Date(unit.returnExpectedAt).toLocaleDateString()} — receive via catalog check-in
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-emerald-600 text-sm">
                      {money(unit.agreement.paidCents)} / {money(unit.agreement.principalCents)}
                      {typeof unit.agreement.progress === 'number' && ` · ${Math.round(unit.agreement.progress * 100)}%`}
                    </div>
                    <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 mt-1 inline-block">
                      agreement status: {unit.agreement.status}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="p-4 text-sm text-stone-400">No financing agreement on this unit.</p>
              )}
            </div>
          </div>

          <div className="space-y-5">
            <IdentityPanel unit={unit} onSaved={setUnit} />
            <TimelinePanel unitId={unit.unitId} refreshKey={timelineRefreshKey} />

            {/*
              THE JOURNEY side of this device's history.
              `TimelinePanel` above is hardware-service's own lifecycle log —
              received, allocated, provisioned, shipped. This is everything the
              ORDER did around it: who verified the payment, when a TID was
              keyed, what was said. Two logs on purpose: one is the device's
              physical life, the other is the commercial one, and merging them
              would put a warehouse scan and a rate negotiation in one list.
            */}
            <UnitConditionPanel unit={unit} onSaved={(u) => { setUnit(u); setTimelineRefreshKey((k) => k + 1); }} />
            <JourneyTimeline
              subject={`unit:${unit.unitId}`}
              title="Order history for this device"
              labels={timelineLabels}
              limit={50}
            />
            {unit.modelId && (
              <JourneyTimeline
                subject={`model:${unit.modelId}`}
                title={`Everything for ${model?.modelName || unit.modelId}`}
                labels={timelineLabels}
                limit={50}
              />
            )}
            {audit.length > 0 && (
              <div className="rounded-xl border border-stone-200 bg-white p-5">
                <h3 className="text-sm font-bold text-stone-800 mb-3">Device history</h3>
                <div className="space-y-0">
                  {audit.map((a: any, i) => {
                    /*
                      ONE TRAIL, SEVERAL KINDS OF EVENT.

                      Agreement actions, badge changes and releases all live in
                      the same AUDIT# rows but carry different fields. Rendering
                      them all through the agreement branch printed a blank line
                      for anything that had no `action`/`amountCents` — worse
                      than not recording it, because the row was there and said
                      nothing. Each kind names itself, and an unknown `type`
                      still prints something rather than an empty line.
                    */
                    const kind = a.type === 'badges_changed' ? 'badge'
                      : a.type === 'unit_released' || a.type === 'unit_wiped' ? 'release'
                        : 'agreement';
                    const dot = kind === 'badge' ? { cls: 'border-stone-200 bg-stone-100', mark: '⬤' }
                      : kind === 'release' ? { cls: 'border-amber-200 bg-amber-100', mark: '↩' }
                        : { cls: 'border-rose-200 bg-rose-100', mark: '$' };
                    return (
                      <div key={i} className="flex gap-3 py-2.5 border-b border-stone-100 last:border-b-0">
                        <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] ${dot.cls}`}>
                          {dot.mark}
                        </div>
                        <div className="min-w-0">
                          {kind === 'badge' ? (
                            <div className="text-xs font-semibold text-stone-800">
                              {(a.added || []).length > 0 && <>Marked {(a.added || []).map(unitBadgeLabel).join(', ')}</>}
                              {(a.added || []).length > 0 && (a.removed || []).length > 0 && ' · '}
                              {(a.removed || []).length > 0 && <>Cleared {(a.removed || []).map(unitBadgeLabel).join(', ')}</>}
                            </div>
                          ) : kind === 'release' ? (
                            <>
                              <div className="text-xs font-semibold text-stone-800">
                                {a.type === 'unit_wiped'
                                  ? 'Wiped and returned to stock'
                                  : 'Unallocated — returned to stock'}
                                {a.fromState ? <span className="font-normal text-stone-500"> from {String(a.fromState).replace(/_/g, ' ')}</span> : null}
                              </div>
                              {/* The order is cleared off the unit, so without
                                  this the trail could not answer the first
                                  question anyone asks: taken off WHAT? */}
                              {a.fromOrderId && (
                                <div className="text-[11px] text-stone-500">
                                  Taken off <span className="font-mono">{String(a.fromOrderId).replace(/^ord_/, '').slice(0, 8).toUpperCase()}</span>
                                  {(a.cleared || []).length ? ` · cleared ${(a.cleared || []).join(', ')}` : ''}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-xs font-semibold capitalize text-stone-800">
                              {a.action ? <>{a.action} — {money(a.amountCents)}</> : String(a.type || 'event').replace(/_/g, ' ')}
                            </div>
                          )}
                          <div className="text-[11px] text-stone-500">
                            {a.reason ? `${a.reason} · ` : ''}by {a.actorEmail || a.actor || 'system'} · {shortDate(a.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>,
  );
};

export default UnitPage;
